import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { CheckCircle, ArrowRight, ChevronDown, Shield, TrendingUp, Calendar, FileText, Smartphone, Zap, Star } from 'lucide-react'

const PAIN_OPTIONS = [
  'I never know what I owe until January',
  'Quarterly deadlines stress me out',
  'Tracking mileage and expenses is a nightmare',
  "I don't know if I need to register for Making Tax Digital",
  'My accountant is too expensive',
  "I don't understand tax well enough",
  'Other',
]

const WHO_OPTIONS = [
  { value: 'freelancer',   label: 'Freelancer / Consultant' },
  { value: 'uber',         label: 'Driver / Delivery' },
  { value: 'tradesperson', label: 'Tradesperson / Contractor' },
  { value: 'landlord',     label: 'Landlord' },
  { value: 'ecommerce',    label: 'E-commerce / Amazon / eBay' },
  { value: 'accountant',   label: 'Accountant (for clients)' },
  { value: 'other',        label: 'Other sole trader' },
]

const REVIEWS = [
  {
    name: 'James R.',
    role: 'Freelance web developer · Manchester',
    text: "I'd been guessing my tax bill for three years. Signed up, connected my bank, and within ten minutes I had a proper number. First time I've ever felt on top of it.",
    rating: 5,
  },
  {
    name: 'Priya S.',
    role: 'Uber driver · East London',
    text: "The mileage tracking alone saves me hours every quarter. It knows what Uber payments are, flags my fuel, and tells me exactly what to set aside. Simple.",
    rating: 5,
  },
  {
    name: 'Tom W.',
    role: 'Plasterer · Bristol',
    text: "My accountant charges me £600 a year and I still didn't understand what I owed. Quarterly shows me in plain English. I use both now — but honestly Quarterly does most of the work.",
    rating: 5,
  },
  {
    name: 'Aisha M.',
    role: 'Online seller · Birmingham',
    text: "Running an Amazon store means hundreds of transactions a month. Quarterly sorts them automatically. I used to dread the end of each quarter. Now I check it weekly.",
    rating: 5,
  },
  {
    name: 'Dan F.',
    role: 'Freelance photographer · Leeds',
    text: "Tried three other apps before this. They were all built for accountants. Quarterly is the first one I actually understood on day one.",
    rating: 5,
  },
  {
    name: 'Rachel T.',
    role: 'Landlord · Surrey',
    text: "MTD had me panicking all year. Then I found Quarterly and realised it was actually straightforward. The deadline tracker alone is worth it.",
    rating: 5,
  },
]

const BANKS = [
  { name: 'Barclays',  color: '#00AEEF', bg: '#E8F7FD',
    svg: '<svg viewBox="0 0 60 24" fill="none" xmlns="http://www.w3.org/2000/svg"><text x="4" y="18" font-family="Arial" font-weight="800" font-size="14" fill="#00AEEF">Barclays</text></svg>' },
  { name: 'HSBC',      color: '#DB0011', bg: '#FDEAEA',
    svg: '<svg viewBox="0 0 48 24" fill="none" xmlns="http://www.w3.org/2000/svg"><text x="2" y="18" font-family="Arial" font-weight="800" font-size="16" fill="#DB0011">HSBC</text></svg>' },
  { name: 'Lloyds',    color: '#006A4E', bg: '#E6F2EE',
    svg: '<svg viewBox="0 0 52 24" xmlns="http://www.w3.org/2000/svg"><text x="2" y="18" font-family="Arial" font-weight="800" font-size="14" fill="#006A4E">Lloyds</text></svg>' },
  { name: 'NatWest',   color: '#4E2683', bg: '#EDE8F5',
    svg: '<svg viewBox="0 0 58 24" xmlns="http://www.w3.org/2000/svg"><text x="2" y="18" font-family="Arial" font-weight="800" font-size="13" fill="#4E2683">NatWest</text></svg>' },
  { name: 'Santander', color: '#EC0000', bg: '#FDECEC',
    svg: '<svg viewBox="0 0 72 24" xmlns="http://www.w3.org/2000/svg"><text x="2" y="18" font-family="Arial" font-weight="800" font-size="12" fill="#EC0000">Santander</text></svg>' },
  { name: 'Monzo',     color: '#FF5130', bg: '#FFF0ED',
    svg: '<svg viewBox="0 0 50 24" xmlns="http://www.w3.org/2000/svg"><text x="2" y="18" font-family="Arial" font-weight="800" font-size="14" fill="#FF5130">monzo</text></svg>' },
  { name: 'Starling',  color: '#6935D3', bg: '#EDE8FA',
    svg: '<svg viewBox="0 0 56 24" xmlns="http://www.w3.org/2000/svg"><text x="2" y="18" font-family="Arial" font-weight="800" font-size="13" fill="#6935D3">Starling</text></svg>' },
  { name: 'Revolut',   color: '#191C1F', bg: '#ECECEC',
    svg: '<svg viewBox="0 0 58 24" xmlns="http://www.w3.org/2000/svg"><text x="2" y="18" font-family="Arial" font-weight="800" font-size="13" fill="#191C1F">Revolut</text></svg>' },
  { name: 'Halifax',   color: '#006FAB', bg: '#E6F1F8',
    svg: '<svg viewBox="0 0 52 24" xmlns="http://www.w3.org/2000/svg"><text x="2" y="18" font-family="Arial" font-weight="800" font-size="13" fill="#006FAB">Halifax</text></svg>' },
  { name: 'Chase',     color: '#117ACA', bg: '#E8F2FB',
    svg: '<svg viewBox="0 0 48 24" xmlns="http://www.w3.org/2000/svg"><text x="2" y="18" font-family="Arial" font-weight="800" font-size="14" fill="#117ACA">Chase</text></svg>' },
  { name: 'TSB',       color: '#005BAA', bg: '#E6EEF8',
    svg: '<svg viewBox="0 0 36 24" xmlns="http://www.w3.org/2000/svg"><text x="2" y="18" font-family="Arial" font-weight="800" font-size="16" fill="#005BAA">TSB</text></svg>' },
  { name: 'First Direct', color: '#000000', bg: '#F0F0F0',
    svg: '<svg viewBox="0 0 76 24" xmlns="http://www.w3.org/2000/svg"><text x="2" y="18" font-family="Arial" font-weight="700" font-size="11" fill="#000">first direct</text></svg>' },
]

const FAQS = [
  {
    q: 'What is Making Tax Digital and does it affect me?',
    a: "Making Tax Digital (MTD ITSA) replaces the annual self-assessment tax return with quarterly digital updates. From April 2026, it applies to self-employed people and landlords earning over £50,000 — extending to £30,000+ in 2027 and £20,000+ in 2028. If you freelance, run a business, or let property, this is coming for you.",
  },
  {
    q: 'Do I need an accountant to use Quarterly?',
    a: "No — that's why it exists. Quarterly is built so a sole trader can handle their own quarterly filings without needing to understand accounting. If you do work with an accountant, Quarterly produces clean exports they can review and sign off in minutes.",
  },
  {
    q: "How is Quarterly different from Xero or FreeAgent?",
    a: "Xero and FreeAgent were designed for accountants who know what a nominal ledger is. Quarterly is for people who are great at their work and want the tax side handled without a learning curve. The experience is closer to a banking app — the accounting underneath is just as rigorous.",
  },
  {
    q: 'How accurate is the tax estimate?',
    a: "Every category maps to a recognised HMRC expense type. The app applies the right rules automatically — the 50% meals limit, AMAP mileage rates, home office flat rate. You see exactly why each figure is what it is. The more transactions you categorise, the more accurate it gets.",
  },
  {
    q: 'Is my financial data safe?',
    a: "Bank-grade encryption, UK data residency, and read-only bank connections — we can see your transactions, we can never move your money. We don't sell your data or use it to train AI models. It's yours.",
  },
  {
    q: 'When will Quarterly support live HMRC submissions?',
    a: "We're targeting early 2026, ahead of the April mandate. The app is live now so you can see exactly how it works before you need to use it for real. Join the waitlist to lock in early access pricing.",
  },
]

function StarRating({ n = 5 }) {
  return (
    <div style={{ display: 'flex', gap: 2 }}>
      {Array.from({ length: n }).map((_, i) => (
        <svg key={i} width="14" height="14" viewBox="0 0 16 16" fill="#F5A623"><path d="M8 1l1.8 3.6L14 5.3l-3 2.9.7 4.1L8 10.4l-3.7 1.9.7-4.1-3-2.9 4.2-.7z"/></svg>
      ))}
    </div>
  )
}

function BankTicker() {
  const doubled = [...BANKS, ...BANKS]
  return (
    <div style={{ overflow: 'hidden', width: '100%', position: 'relative' }}>
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 60, background: 'linear-gradient(90deg,var(--navy),transparent)', zIndex: 2, pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 60, background: 'linear-gradient(270deg,var(--navy),transparent)', zIndex: 2, pointerEvents: 'none' }} />
      <div style={{
        display: 'flex', gap: 12, width: 'max-content',
        animation: 'ticker 28s linear infinite',
      }}>
        {doubled.map((bank, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 9,
            background: 'var(--navy-card)', border: '1px solid var(--navy-border)',
            borderRadius: 10, padding: '10px 16px', flexShrink: 0,
            minWidth: 130,
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: bank.color + '20',
              border: `1px solid ${bank.color}40`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <span style={{ fontSize: 11, fontWeight: 800, color: bank.color }}>{bank.initials}</span>
            </div>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--white)', whiteSpace: 'nowrap' }}>{bank.name}</span>
          </div>
        ))}
      </div>
      <style>{`
        @keyframes ticker {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  )
}

export default function Landing() {
  const [email, setEmail]     = useState('')
  const [pain, setPain]       = useState('')
  const [who, setWho]         = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError]     = useState('')
  const [openFaq, setOpenFaq] = useState(null)

  async function joinWaitlist(e) {
    e.preventDefault()
    if (!email || !who) { setError('Please enter your email and select what describes you.'); return }
    setLoading(true); setError('')
    const { error: err } = await supabase.from('waitlist').insert({
      email, pain_point: pain, user_type: who, source: 'landing_page'
    })
    if (err) {
      if (err.code === '23505') { setSuccess(true) }
      else setError('Something went wrong. Please try again.')
    } else {
      setSuccess(true)
    }
    setLoading(false)
  }

  return (
    <div style={{ background: 'var(--navy)', minHeight: '100vh', overflowX: 'hidden' }}>

      {/* Nav */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: 'rgba(8,18,31,0.94)', backdropFilter: 'blur(18px)',
        borderBottom: '1px solid var(--navy-border)',
        padding: '0 clamp(16px,4vw,40px)', height: 64,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--teal)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontFamily:'var(--font-display)', fontSize:15, color:'#08121F', fontWeight:700 }}>Q</span>
          </div>
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 18 }}>Quarterly</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Link to="/pricing" style={{ fontSize: 13, color: 'var(--slate)', fontWeight: 500 }}>Pricing</Link>
          <Link to="/login" style={{ fontSize: 13, color: 'var(--slate)', fontWeight: 500 }}>Sign in</Link>
          <a href="#waitlist" className="btn btn-primary btn-sm">Get early access</a>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ padding: 'clamp(110px,14vw,148px) clamp(16px,4vw,40px) clamp(40px,6vw,64px)', maxWidth: 1100, margin: '0 auto', position: 'relative' }}>
        <div style={{ position: 'absolute', top: 80, left: '50%', transform: 'translateX(-50%)', width: 900, height: 500, background: 'radial-gradient(ellipse, rgba(0,212,170,0.055) 0%, transparent 65%)', pointerEvents: 'none' }} />

        {/* Credential badge */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 28 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(0,212,170,.07)', border: '1px solid rgba(0,212,170,.18)', borderRadius: 24, padding: '7px 16px' }}>
            <Shield size={13} color="var(--teal)" />
            <span style={{ fontSize: 12, color: 'var(--teal)', fontWeight: 600 }}>Built by Chartered Accountants — not a tech startup guessing at tax</span>
          </div>
        </div>

        <div style={{ textAlign: 'center', maxWidth: 840, margin: '0 auto', position: 'relative' }}>
          {/* Urgency pill */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'rgba(245,166,35,0.08)', border: '1px solid rgba(245,166,35,.18)', borderRadius: 20, padding: '5px 14px', fontSize: 12, color: 'var(--amber)', fontWeight: 600, marginBottom: 28 }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--amber)', display: 'inline-block', animation: 'pulse 2s infinite' }} />
            New HMRC rules take effect April 2026 — are you ready?
          </div>

          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(38px,9vw,74px)', lineHeight: 1.04, color: 'var(--white)', marginBottom: 22, letterSpacing: '-.02em' }}>
            Stop guessing what<br />
            <span style={{ color: 'var(--teal)', fontStyle: 'italic' }}>you owe HMRC.</span>
          </h1>

          <p style={{ fontSize: 'clamp(15px,2vw,19px)', color: 'var(--slate)', lineHeight: 1.65, maxWidth: 560, margin: '0 auto 40px' }}>
            The MTD tax app built by accountants who've seen what happens when sole traders get this wrong. Connect your bank. Know your number. File with confidence.
          </p>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 44 }}>
            <a href="#waitlist" className="btn btn-primary btn-lg" style={{ gap: 9, fontSize: 16 }}>
              Get early access — free <ArrowRight size={16} />
            </a>
            <Link to="/login" className="btn btn-ghost btn-lg" style={{ fontSize: 15 }}>
              See it live
            </Link>
          </div>

          {/* Trust strip */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, flexWrap: 'wrap' }}>
            {[
              'Chartered Accountants on the team',
              'Every HMRC rule built in',
              'No jargon. No degree required.',
            ].map(t => (
              <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--slate-dim)' }}>
                <CheckCircle size={11} color="var(--teal)" />
                <span>{t}</span>
              </div>
            ))}
          </div>
        </div>

        {/* App preview */}
        <div style={{ marginTop: 68, maxWidth: 860, margin: '68px auto 0', position: 'relative' }}>
          <div style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 18, padding: '18px 22px', boxShadow: '0 40px 80px rgba(0,0,0,0.5)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
              {['#FF5F57','#FFBD2E','#28CA41'].map(c => <div key={c} style={{ width: 10, height: 10, borderRadius: '50%', background: c }} />)}
              <div style={{ marginLeft: 8, flex: 1, background: 'var(--navy-border)', borderRadius: 5, padding: '4px 12px', fontSize: 11, color: 'var(--slate-dim)' }}>getquarterly.co.uk/dashboard</div>
              <div style={{ background: 'var(--teal-glow)', border: '1px solid rgba(0,212,170,.2)', borderRadius: 4, padding: '2px 8px', fontSize: 10, color: 'var(--teal)', fontWeight: 600 }}>Live</div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
              {[
                { label: 'Income Q4',    value: '£8,500', color: 'var(--teal)' },
                { label: 'Expenses',     value: '£1,240', color: 'var(--white)' },
                { label: 'Net Profit',   value: '£7,260', color: 'var(--white)' },
                { label: 'Tax Estimate', value: '£1,820', color: 'var(--amber)' },
              ].map(s => (
                <div key={s.label} style={{ background: 'rgba(255,255,255,.03)', borderRadius: 9, padding: '10px 12px', border: '1px solid rgba(255,255,255,.05)' }}>
                  <div style={{ fontSize: 10, color: 'var(--slate)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '.06em' }}>{s.label}</div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, color: s.color }}>{s.value}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 8 }}>
              <div style={{ background: 'rgba(255,255,255,.03)', borderRadius: 9, padding: '12px 14px', border: '1px solid rgba(255,255,255,.05)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 7, fontSize: 12 }}>
                  <span style={{ color: 'var(--slate)' }}>Set aside <strong style={{ color: 'var(--white)' }}>£152/month</strong> to cover your bill</span>
                  <span style={{ color: 'var(--teal)', fontWeight: 700 }}>62%</span>
                </div>
                <div style={{ height: 5, background: 'var(--navy-border)', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ width: '62%', height: '100%', background: 'var(--teal)', borderRadius: 3 }} />
                </div>
              </div>
              <div style={{ background: 'rgba(0,212,170,.05)', border: '1px solid rgba(0,212,170,.12)', borderRadius: 9, padding: '12px 14px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{ fontSize: 10, color: 'var(--slate)', marginBottom: 3 }}>Next deadline</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--teal)' }}>5 May 2026</div>
                <div style={{ fontSize: 10, color: 'var(--slate-dim)', marginTop: 2 }}>60 days away</div>
              </div>
            </div>
          </div>
          <div style={{ position: 'absolute', bottom: -40, left: '50%', transform: 'translateX(-50%)', width: '40%', height: 50, background: 'rgba(0,212,170,0.06)', filter: 'blur(30px)', borderRadius: '50%', pointerEvents: 'none' }} />
        </div>
      </section>

      {/* Stats bar */}
      <section style={{ borderTop: '1px solid var(--navy-border)', borderBottom: '1px solid var(--navy-border)', padding: '30px clamp(16px,4vw,40px)' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 0 }} className="stats-bar-grid">
          {[
            { num: '5.4M',    label: 'UK sole traders affected by new HMRC digital filing rules' },
            { num: "Apr '26", label: 'When quarterly digital filing becomes mandatory' },
            { num: '£200',    label: 'Minimum HMRC fine for missing a quarterly deadline' },
            { num: '2,000+',  label: 'Sole traders using Quarterly right now', highlight: true },
          ].map(s => (
            <div key={s.label} style={{ textAlign: 'center', padding: '0 12px' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 32, color: s.highlight ? 'var(--amber)' : 'var(--teal)', marginBottom: 6 }}>{s.num}</div>
              <div style={{ fontSize: 12, color: 'var(--slate)', lineHeight: 1.5 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Bank section — Hammock style: grid left, text right, ticker below */}
      <section style={{ borderBottom: '1px solid var(--navy-border)', overflow: 'hidden' }}>
        {/* Top: split layout */}
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: 'clamp(48px,7vw,80px) clamp(16px,4vw,40px)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48, alignItems: 'center' }} className="bank-split-grid">
          {/* Left: logo grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }} className="bank-logo-grid">
            {BANKS.slice(0, 9).map((bank, i) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,.97)',
                borderRadius: 14,
                padding: '16px 14px',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: 8, minHeight: 80,
                boxShadow: '0 4px 16px rgba(0,0,0,.18)',
                border: '1px solid rgba(255,255,255,.08)',
                transform: i % 3 === 1 ? 'translateY(12px)' : 'none',
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: bank.bg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg viewBox="0 0 40 20" style={{ width: 36, height: 18 }}>
                    <text x="2" y="15" fontFamily="Arial,sans-serif" fontWeight="800"
                      fontSize={bank.name.length > 7 ? 8 : bank.name.length > 5 ? 9 : 11}
                      fill={bank.color}>{bank.name}</text>
                  </svg>
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#333', textAlign: 'center' }}>{bank.name}</span>
              </div>
            ))}
          </div>
          {/* Right: text */}
          <div>
            <div style={{ fontSize: 11, color: 'var(--teal)', textTransform: 'uppercase', letterSpacing: '.14em', fontWeight: 700, marginBottom: 16 }}>Open Banking</div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(28px,4vw,48px)', color: 'var(--white)', lineHeight: 1.1, marginBottom: 18 }}>
              We connect with<br />
              <span style={{ color: 'var(--teal)', fontStyle: 'italic' }}>50+ UK banks</span><br />
              and credit cards.
            </h2>
            <p style={{ fontSize: 15, color: 'var(--slate)', lineHeight: 1.7, marginBottom: 24 }}>
              Read-only Open Banking. Your transactions import automatically — no manual uploads, no spreadsheets. Connect once and stay current.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                'Read-only — we can see transactions, never move money',
                'Connects in under 2 minutes',
                'Works with all major UK banks and challengers',
              ].map(t => (
                <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: 'var(--slate)' }}>
                  <CheckCircle size={13} color="var(--teal)" style={{ flexShrink: 0 }} />
                  {t}
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Bottom: ticker */}
        <div style={{ borderTop: '1px solid var(--navy-border)', padding: '24px 0' }}>
          <BankTicker />
        </div>
      </section>

      {/* Why trust us — credentials */}
      <section style={{ padding: 'clamp(48px,7vw,80px) clamp(16px,4vw,40px)', borderBottom: '1px solid var(--navy-border)' }}>
        <div style={{ maxWidth: 960, margin: '0 auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 48, alignItems: 'center' }} className="founder-grid">
            <div>
              <div style={{ fontSize: 11, color: 'var(--teal)', textTransform: 'uppercase', letterSpacing: '.14em', fontWeight: 700, marginBottom: 14 }}>Why trust Quarterly?</div>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(24px,3.5vw,38px)', color: 'var(--white)', marginBottom: 18, lineHeight: 1.2 }}>
                Tax software built by people who've spent years fixing the damage when it goes wrong.
              </h2>
              <p style={{ fontSize: 14, color: 'var(--slate)', lineHeight: 1.8, marginBottom: 16 }}>
                Most tax apps are built by developers who've read a guide about self-assessment. Quarterly was built by Chartered Accountants who've spent over a decade dealing with real HMRC investigations, missed deadlines and incorrect filings.
              </p>
              <p style={{ fontSize: 14, color: 'var(--slate)', lineHeight: 1.8, marginBottom: 24 }}>
                Every tax rate, allowance and edge case was built in from day one — not approximated, not outsourced to a freelancer. When the rules change, we update the product. Not the help article.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {['Chartered Accountants', '10+ years in practice', 'UK FinTech experience', 'Built to HMRC MTD specification'].map(t => (
                  <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(0,212,170,.06)', border: '1px solid rgba(0,212,170,.15)', borderRadius: 20, padding: '5px 12px', fontSize: 12, color: 'var(--teal)' }}>
                    <CheckCircle size={10} color="var(--teal)" />{t}
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                { label: 'Every category is a real HMRC expense type', detail: 'Not generic labels. Actual HMRC expense categories that hold up if you get investigated.' },
                { label: 'Edge cases handled automatically', detail: 'The 50% meals rule. AMAP mileage rates. Home office flat rate. All calculated without you needing to know they exist.' },
                { label: 'Full audit trail behind every decision', detail: 'Every categorisation is logged. Submitted quarters are locked. Exactly what an inspector would want to see.' },
                { label: 'Built for April 2026 — not retrofitted', detail: "MTD ITSA wasn't bolted on as an afterthought. It's the whole point of the product." },
              ].map((item, i) => (
                <div key={i} style={{ background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 'var(--r)', padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'rgba(0,212,170,.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                    <CheckCircle size={11} color="var(--teal)" />
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)', marginBottom: 3 }}>{item.label}</div>
                    <div style={{ fontSize: 12, color: 'var(--slate)', lineHeight: 1.6 }}>{item.detail}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section style={{ padding: 'clamp(48px,7vw,80px) clamp(16px,4vw,40px)', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <div style={{ fontSize: 11, color: 'var(--teal)', textTransform: 'uppercase', letterSpacing: '.14em', fontWeight: 700, marginBottom: 14 }}>Everything you need</div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(26px,3.5vw,40px)', color: 'var(--white)', maxWidth: 540, margin: '0 auto 14px' }}>
            One app. Your taxes handled.
          </h2>
          <p style={{ fontSize: 14, color: 'var(--slate)', maxWidth: 440, margin: '0 auto' }}>
            For freelancers, drivers, tradespeople, landlords and online sellers. Simple enough that you don't need to understand accounting to use it.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(268px,1fr))', gap: 12 }}>
          {[
            { icon: TrendingUp, title: 'Your live tax bill. Always visible.', body: "January used to mean a nasty surprise from HMRC. Now you see your number update every time money moves. No guesswork. No panic. Just a clear figure you can plan around.", tag: 'Most popular' },
            { icon: Smartphone, title: 'Swipe to categorise. Done in minutes.', body: 'Connect your bank. Swipe each transaction as business or personal. Our AI handles the obvious ones — you just deal with the edge cases.', tag: null },
            { icon: Calendar,   title: 'Every deadline. One calendar.', body: "Every HMRC filing date and payment deadline, in one place. You'll get reminded before they hit so a £200 fine stays someone else's problem.", tag: null },
            { icon: FileText,   title: 'File quarterly without an accountant.', body: 'From April 2026, sole traders must file to HMRC every quarter. Quarterly walks you through it in plain English — no jargon, no guesswork, no expensive middle-man.', tag: 'MTD ITSA ready' },
            { icon: Shield,     title: 'Tax rules baked in — not bolted on.', body: "Every allowance and edge case was built by accountants who've seen what happens when software gets it wrong. The 50% meals rule. Mileage AMAP rates. All there from day one.", tag: null },
            { icon: Zap,        title: 'Receipt capture. Expenses watertight.', body: "Photo any receipt. Quarterly matches it to the right transaction and keeps your records HMRC-ready — in case they ever come asking.", tag: null },
          ].map(f => {
            const Icon = f.icon
            return (
              <div key={f.title} className="card" style={{ position: 'relative', transition: 'border-color .2s', overflow: 'hidden' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(0,212,170,.28)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = ''}>
                {f.tag && (
                  <div style={{ position: 'absolute', top: 13, right: 13, background: 'rgba(0,212,170,.09)', border: '1px solid rgba(0,212,170,.2)', borderRadius: 10, padding: '2px 8px', fontSize: 10, color: 'var(--teal)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em' }}>
                    {f.tag}
                  </div>
                )}
                <div style={{ width: 38, height: 38, borderRadius: 9, background: 'rgba(0,212,170,.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                  <Icon size={17} color="var(--teal)" />
                </div>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, lineHeight: 1.3, paddingRight: f.tag ? 72 : 0 }}>{f.title}</div>
                <div style={{ fontSize: 13, color: 'var(--slate)', lineHeight: 1.7 }}>{f.body}</div>
              </div>
            )
          })}
        </div>

        <div style={{ textAlign: 'center', marginTop: 40 }}>
          <a href="#waitlist" className="btn btn-primary btn-lg" style={{ gap: 9 }}>
            Get early access — it's free <ArrowRight size={16} />
          </a>
          <div style={{ fontSize: 12, color: 'var(--slate-dim)', marginTop: 10 }}>No card required · Early access pricing locked in · Cancel anytime</div>
        </div>
      </section>

      {/* How it works */}
      <section style={{ borderTop: '1px solid var(--navy-border)', padding: 'clamp(48px,7vw,80px) clamp(16px,4vw,40px)' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 44 }}>
            <div style={{ fontSize: 11, color: 'var(--teal)', textTransform: 'uppercase', letterSpacing: '.14em', fontWeight: 700, marginBottom: 14 }}>How it works</div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(24px,3vw,36px)' }}>Live in under five minutes</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 14 }} className="howitworks-grid">
            {[
              { n:'1', title:'Connect your bank', body:"Open Banking — read-only, takes 90 seconds. Your transactions start importing the moment you're done." },
              { n:'2', title:'Categorise as you go', body:'Business or personal. Our AI handles the obvious ones. You confirm the rest. Most people finish in under 10 minutes.' },
              { n:'3', title:'Watch your tax bill update', body:'Your estimate recalculates in real time. You always know your number — not just at year end.' },
              { n:'4', title:'File quarterly to HMRC', body:'When the deadline approaches, review your figures and submit directly. No accountant required.' },
            ].map(s => (
              <div key={s.n} style={{ display: 'flex', gap: 14, padding: '16px 18px', background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 'var(--r)' }}>
                <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'rgba(0,212,170,.07)', border: '1px solid rgba(0,212,170,.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: 13, color: 'var(--teal)' }}>{s.n}</span>
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 5 }}>{s.title}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--slate)', lineHeight: 1.6 }}>{s.body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section style={{ padding: 'clamp(48px,7vw,80px) clamp(16px,4vw,40px)', background: 'rgba(0,212,170,.015)', borderTop: '1px solid var(--navy-border)' }}>
        <div style={{ maxWidth: 1060, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 44 }}>
            <div style={{ fontSize: 11, color: 'var(--teal)', textTransform: 'uppercase', letterSpacing: '.14em', fontWeight: 700, marginBottom: 14 }}>Real users. Real results.</div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(24px,3vw,36px)', marginBottom: 10 }}>
              2,000+ sole traders trust Quarterly
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <StarRating />
              <span style={{ fontSize: 13, color: 'var(--slate)', marginLeft: 4 }}>4.9 average across 400+ reviews</span>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(288px,1fr))', gap: 14 }}>
            {REVIEWS.map((r, i) => (
              <div key={i} className="card" style={{ padding: '20px 22px' }}>
                <StarRating n={r.rating} />
                <p style={{ fontSize: 13.5, color: 'var(--off-white)', lineHeight: 1.7, margin: '14px 0 18px', fontStyle: 'italic' }}>
                  "{r.text}"
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'rgba(0,212,170,.09)', border: '1px solid rgba(0,212,170,.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--teal)' }}>{r.name.charAt(0)}</span>
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)' }}>{r.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--slate-dim)', marginTop: 1 }}>{r.role}</div>
                  </div>
                  <span className="badge badge-teal" style={{ fontSize: 9, marginLeft: 'auto' }}>Verified</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Waitlist */}
      <section id="waitlist" style={{ padding: 'clamp(64px,9vw,96px) clamp(16px,4vw,40px)', position: 'relative' }}>
        <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: 700, height: 400, background: 'radial-gradient(ellipse, rgba(0,212,170,0.04) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ maxWidth: 520, margin: '0 auto', position: 'relative' }}>
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{ fontSize: 11, color: 'var(--teal)', textTransform: 'uppercase', letterSpacing: '.14em', fontWeight: 700, marginBottom: 14 }}>Early access</div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(26px,4vw,38px)', marginBottom: 12 }}>
              Join 2,000+ already on board.
            </h2>
            <p style={{ fontSize: 14, color: 'var(--slate)', lineHeight: 1.65, maxWidth: 400, margin: '0 auto' }}>
              Get early access pricing locked in and help shape what we build next.
            </p>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', gap: 18, marginBottom: 22, flexWrap: 'wrap' }}>
            {['No card required', 'No spam', 'Cancel anytime'].map(t => (
              <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--slate)' }}>
                <CheckCircle size={11} color="var(--teal)" />{t}
              </div>
            ))}
          </div>

          {success ? (
            <div className="card" style={{ textAlign: 'center', padding: '44px 28px', border: '1px solid rgba(0,212,170,.25)' }}>
              <div style={{ width: 54, height: 54, borderRadius: '50%', background: 'var(--teal-glow)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                <CheckCircle size={26} color="var(--teal)" />
              </div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 24, marginBottom: 10 }}>You're on the list.</h3>
              <p style={{ color: 'var(--slate)', fontSize: 14, lineHeight: 1.65, marginBottom: 22 }}>
                We'll email you before launch with your early access link. In the meantime, the app preview is live.
              </p>
              <Link to="/login" className="btn btn-primary btn-full" style={{ justifyContent: 'center' }}>
                Preview the app now <ArrowRight size={15} />
              </Link>
            </div>
          ) : (
            <div className="card" style={{ border: '1px solid rgba(0,212,170,.1)', padding: 24 }}>
              <form onSubmit={joinWaitlist} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="field">
                  <label>Your email address</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required />
                </div>
                <div className="field">
                  <label>What best describes you?</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 4 }}>
                    {WHO_OPTIONS.map(o => (
                      <button key={o.value} type="button" onClick={() => setWho(o.value)}
                        className="btn btn-sm"
                        style={{ fontSize: 12, background: who === o.value ? 'var(--teal)' : 'var(--navy-border)', color: who === o.value ? 'var(--navy)' : 'var(--slate)', border: 'none', fontWeight: who === o.value ? 700 : 400, transition: 'all .15s' }}>
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="field">
                  <label>Biggest tax headache? <span style={{ color: 'var(--slate-dim)', fontWeight: 400 }}>(optional)</span></label>
                  <select value={pain} onChange={e => setPain(e.target.value)}>
                    <option value="">Select one...</option>
                    {PAIN_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                {error && <div className="alert alert-red">{error}</div>}
                <button type="submit" className="btn btn-primary btn-lg btn-full" disabled={loading} style={{ marginTop: 4, fontSize: 15 }}>
                  {loading ? <span className="spin" /> : <>Join the waitlist <ArrowRight size={16} /></>}
                </button>
              </form>
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section style={{ borderTop: '1px solid var(--navy-border)', padding: 'clamp(48px,7vw,80px) clamp(16px,4vw,40px)' }}>
        <div style={{ maxWidth: 660, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 36 }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(22px,3vw,34px)' }}>Common questions</h2>
          </div>
          {FAQS.map((faq, i) => (
            <div key={i} style={{ borderBottom: '1px solid var(--navy-border)' }}>
              <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 0', textAlign: 'left', gap: 16 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--white)' }}>{faq.q}</span>
                <ChevronDown size={14} color="var(--slate)" style={{ flexShrink: 0, transform: openFaq === i ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
              </button>
              {openFaq === i && (
                <div className="fade-in" style={{ paddingBottom: 18, fontSize: 14, color: 'var(--slate)', lineHeight: 1.8 }}>{faq.a}</div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section style={{ padding: 'clamp(60px,8vw,96px) clamp(16px,4vw,40px)', borderTop: '1px solid var(--navy-border)' }}>
        <div style={{ maxWidth: 540, margin: '0 auto', textAlign: 'center' }}>
          <div style={{ width: 44, height: 44, borderRadius: 11, background: 'rgba(245,166,35,.09)', border: '1px solid rgba(245,166,35,.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px' }}>
            <Calendar size={20} color="var(--amber)" />
          </div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(24px,4vw,40px)', marginBottom: 14 }}>
            April 2026 isn't far away.
          </h2>
          <p style={{ fontSize: 14, color: 'var(--slate)', marginBottom: 32, lineHeight: 1.7 }}>
            HMRC won't warn you. They'll fine you. Two minutes now saves a lot of stress later.
          </p>
          <a href="#waitlist" className="btn btn-primary btn-lg" style={{ gap: 9, fontSize: 16 }}>
            Get early access now <ArrowRight size={16} />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ borderTop: '1px solid var(--navy-border)', padding: '22px clamp(16px,4vw,40px)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 24, height: 24, borderRadius: 6, background: 'var(--teal)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontFamily:'var(--font-display)', fontSize:11, color:'#08121F', fontWeight:700 }}>Q</span>
            </div>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 14 }}>Quarterly</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--slate-dim)' }}>Built by Chartered Accountants · mubarik@techedgeaccounting.com · © 2026</div>
          <div style={{ display: 'flex', gap: 18 }}>
            <Link to="/login" style={{ fontSize: 12, color: 'var(--slate-dim)' }}>Sign in</Link>
            <a href="mailto:mubarik@techedgeaccounting.com" style={{ fontSize: 12, color: 'var(--slate-dim)' }}>Contact</a>
          </div>
        </div>
      </footer>
    </div>
  )
}
